import type { SystemSettings } from "@gooner/shared";

/**
 * Does the Browse grid hold a cell for its in-feed ad?
 *
 * The cell is laid out by Browse, not by AdSlot, because AdSlot renders nothing until
 * the settings and the session have answered and nothing again once a zone fails to
 * fill — and every one of those flips moved the tiles after it. Measured on prod: the
 * slot landed ~0.5s after the grid and moved tile 9 two columns, so a click aimed at one
 * clip in that window opened another; an unfilled zone collapsed 10s later and moved
 * them back.
 *
 *  - "reserve": not yet known. Hold the space, so the common case (a guest on a site
 *    with ads on) never moves.
 *  - "ad": an ad belongs here. The cell holds its space for the rest of the page,
 *    filled or not — an empty box is better than a grid that moves under the cursor.
 *  - "none": nothing for this visitor (ads off, no zone). No cell.
 *
 * Mirrors AdSlot's own gates (ads.enabled + a zone) so the two cannot disagree about
 * whether something is coming. A member gets the cell too, since 2026-11-03: their slot
 * shows a shop product instead of the network's ad, and tells Browse (onHouse) to let the
 * cell take the card's height, or none when there is no product to show.
 */
export type BrowseAdCell = "reserve" | "ad" | "none";

export function browseAdCell(
  settings: Pick<SystemSettings, "ads"> | null,
  session: { ready: boolean; goldUntil?: number | null },
  _now: number,
): BrowseAdCell {
  if (!settings || !session.ready) return "reserve";
  const zone = settings.ads?.enabled ? settings.ads.zones?.browseInFeed?.trim() : "";
  if (!zone) return "none";
  return "ad";
}

/** The cell's floor: the "Advertisement" caption (10px type, ~15px line + 4px gap) over
 *  the 250px unit. Without it a cell whose ad has not mounted is a zero-height row on a
 *  two-column phone grid, and the row grows when the unit lands. */
export const BROWSE_AD_CELL_MIN_HEIGHT = 270;
